import { useEffect, useMemo, useState } from 'react'
import SiteHeader from '../components/SiteHeader'
import SiteFooter from '../components/SiteFooter'
import { useAnalyticsPageview } from '../hooks/useAnalyticsPageview'
import { useDocumentMeta } from '../hooks/useDocumentMeta'
import '../styles/insights.css'

interface InsightsItem {
  id: number
  headline: string
  url: string
  source: string
  sector: string
  published_at: string
  // Admin-editable via the dashboard's Published-tab drag-and-drop —
  // lower sorts first. Null for nothing (every published row gets one at
  // publish time), but treated as "sort last" defensively either way. Not
  // per-sector: it's one global ranking, but sorting an already
  // sector-filtered subset by it still yields the right top-to-bottom
  // order within that sector — gaps in the numbers between sectors don't
  // matter, only relative order does.
  display_order: number | null
}

// Same-origin route (server/src/routes/documents.ts) rather than a direct
// storage.googleapis.com link — the GCS bucket behind it stays fully
// private (no allUsers grant); only the server's own runtime service
// account can read from it, and the public reaches this file the same way
// they reach every other page on the site, through the load balancer.
const FEATURED_REPORT_URL = '/documents/Selcius_Ag_Markets_Update062026.pdf'

type SortOption = 'curated' | 'newest' | 'oldest' | 'sector' | 'source'

const SORT_OPTIONS: { value: SortOption; label: string }[] = [
  { value: 'curated', label: 'Curated' },
  { value: 'newest', label: 'Newest' },
  { value: 'oldest', label: 'Oldest' },
  { value: 'sector', label: 'Sector A–Z' },
  { value: 'source', label: 'Source A–Z' },
]

export default function InsightsPage() {
  useAnalyticsPageview()
  useDocumentMeta('Insights | Selcius Partners', {
    description:
      "A curated selection of agriculture, food, and bioeconomy sector coverage Selcius considers worth a client's time. Updated regularly.",
  })
  const [items, setItems] = useState<InsightsItem[]>([])
  const [sectors, setSectors] = useState<string[]>([])
  const [selectedSector, setSelectedSector] = useState<string | null>(null)
  const [sortBy, setSortBy] = useState<SortOption>('newest')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    // Fetch sectors
    fetch('/api/sectors')
      .then(res => {
        if (!res.ok) throw new Error(`Failed to fetch sectors (${res.status})`)
        return res.json()
      })
      .then(setSectors)
      .catch(e => console.error('Failed to fetch sectors:', e))
  }, [])

  useEffect(() => {
    setLoading(true)
    setError(null)
    const url = selectedSector
      ? `/api/insights-items?sector=${encodeURIComponent(selectedSector)}`
      : '/api/insights-items'

    fetch(url)
      .then(res => {
        if (!res.ok) throw new Error(`Failed to fetch insights items (${res.status})`)
        return res.json()
      })
      .then(setItems)
      .catch(e => {
        console.error('Failed to fetch items:', e)
        setError('Failed to load insights')
      })
      .finally(() => setLoading(false))
  }, [selectedSector])

  // Sorting is client-side — the curated set for a given week is small, and
  // this avoids a re-fetch (with its own loading flash) just to reorder
  // rows already on the page. "Date" here is published_at (when the item
  // went live on the site), the only date the API returns — there's no
  // separately-tracked original-article-publish date.
  const sortedItems = useMemo(() => {
    const copy = [...items]
    switch (sortBy) {
      case 'curated':
        return copy.sort((a, b) => (a.display_order ?? Infinity) - (b.display_order ?? Infinity))
      case 'oldest':
        return copy.sort((a, b) => a.published_at.localeCompare(b.published_at))
      case 'sector':
        return copy.sort(
          (a, b) => a.sector.localeCompare(b.sector) || b.published_at.localeCompare(a.published_at)
        )
      case 'source':
        return copy.sort(
          (a, b) => a.source.localeCompare(b.source) || b.published_at.localeCompare(a.published_at)
        )
      case 'newest':
      default:
        return copy.sort((a, b) => b.published_at.localeCompare(a.published_at))
    }
  }, [items, sortBy])

  return (
    <div className="insights-page">
      <SiteHeader />
      <div className="insights-header">
        <h1>Insights</h1>
        <p className="insights-subtitle">The latest across the sectors on which we focus</p>
        <p className="ai-disclosure">Selected with AI assistance, reviewed by our team before publishing</p>
      </div>

      <section className="featured-report">
        <div className="featured-report-inner">
          <span className="featured-report-label">Selcius Research</span>
          <h2>Agricultural Markets: Mid-Year Update and Six-Month Outlook</h2>
          <a
            className="featured-report-download"
            href={FEATURED_REPORT_URL}
            target="_blank"
            rel="noopener noreferrer"
          >
            Download PDF
          </a>
        </div>
      </section>

      <div className="insights-container">
        <div className="insights-toolbar">
          {/* Sector filter pills */}
          <div className="sector-filters">
            <button
              className={`sector-pill ${selectedSector === null ? 'active' : ''}`}
              onClick={() => setSelectedSector(null)}
            >
              All Sectors
            </button>
            {sectors.map(sector => (
              <button
                key={sector}
                className={`sector-pill ${selectedSector === sector ? 'active' : ''}`}
                onClick={() => setSelectedSector(sector)}
              >
                {sector}
              </button>
            ))}
          </div>

          {/* Sort controls */}
          <div className="sort-controls">
            <span className="sort-label">Sort by</span>
            {SORT_OPTIONS.map(option => (
              <button
                key={option.value}
                className={`sector-pill ${sortBy === option.value ? 'active' : ''}`}
                onClick={() => setSortBy(option.value)}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>

        {/* Items list */}
        {loading ? (
          <div className="loading">Loading articles...</div>
        ) : error ? (
          <div className="error">{error}</div>
        ) : sortedItems.length === 0 ? (
          <div className="empty">No articles in this sector yet.</div>
        ) : (
          <div className="insights-items">
            {sortedItems.map(item => (
              <article key={item.id} className="insights-item">
                <div className="item-content">
                  <h2 className="item-headline">
                    <a href={item.url} target="_blank" rel="noopener noreferrer">
                      {item.headline}
                    </a>
                  </h2>
                  <div className="item-meta">
                    <span className="item-source">{item.source}</span>
                    <span className="item-sector">{item.sector}</span>
                    {item.published_at && (
                      <time className="item-date">
                        {new Date(item.published_at).toLocaleDateString()}
                      </time>
                    )}
                  </div>
                </div>
              </article>
            ))}
          </div>
        )}

        {/* Broker-dealer disclosure */}
        <footer className="insights-footer">
          <p className="disclosure">
            This list is compiled with AI assistance from public news sources and reviewed by our team before
            publishing. It is provided for informational purposes only and does not constitute investment advice.
            Selcius Partners, an independent middle-market investment bank, curates these insights for clients and
            partners in M&A, capital markets, and related sectors.
          </p>
        </footer>
      </div>

      <SiteFooter />
    </div>
  )
}
